import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Select, Space, Spin, Switch, Typography } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../../utils/feedback';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  CONFIRMATION_NOTICE_ROLES,
  ConfirmationNoticeRecipientsDto,
  ConfirmationNoticeRole,
  NotificationUserOptionDto,
  fetchConfirmationNoticeRecipients,
  fetchNotificationUserOptions,
  saveConfirmationNoticeRecipients,
} from '../api/confirmationNoticeRecipientsApi';

const { Text, Paragraph } = Typography;

/**
 * ADMIN card: recipients of the e-mail sent after a customer order is
 * confirmed (stock level per line). Default = every ADMIN and OPERATIONS user.
 */
const ConfirmationNoticeRecipientsCard: React.FC = () => {
  const { t } = useTranslation(['notifications', 'common']);
  const feedback = useFeedback();
  const [config, setConfig] = useState<ConfirmationNoticeRecipientsDto | null>(null);
  const [users, setUsers] = useState<NotificationUserOptionDto[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([fetchConfirmationNoticeRecipients(), fetchNotificationUserOptions()])
      .then(([loadedConfig, loadedUsers]) => {
        if (!active) return;
        setConfig(loadedConfig);
        setUsers(loadedUsers);
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      });
    return () => { active = false; };
  }, []);

  const title = t('notifications:confirmationNotice.title');

  if (loadFailed) {
    return (
      <Card title={title} style={{ marginTop: 24 }}>
        <Alert type="error" showIcon message={t('notifications:confirmationNotice.loadFailed')} />
      </Card>
    );
  }

  if (!config) {
    return (
      <Card title={title} style={{ marginTop: 24 }}>
        <Spin />
      </Card>
    );
  }

  const nobodySelected = config.includeRoles.length === 0 && !config.includeAccountManager && config.userIds.length === 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveConfirmationNoticeRecipients(config);
      setConfig(saved);
      feedback.success(t('notifications:confirmationNotice.saved'));
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'notifications:confirmationNotice.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title={title} style={{ marginTop: 24 }}>
      <Paragraph type="secondary">{t('notifications:confirmationNotice.description')}</Paragraph>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong>{t('notifications:confirmationNotice.roles')}</Text>
          <div style={{ marginTop: 8 }}>
            <Checkbox.Group
              value={config.includeRoles}
              onChange={(values) => setConfig({
                ...config,
                includeRoles: CONFIRMATION_NOTICE_ROLES.filter((role) => (values as string[]).includes(role)),
              })}
              options={CONFIRMATION_NOTICE_ROLES.map((role: ConfirmationNoticeRole) => ({
                value: role,
                label: t(`notifications:confirmationNotice.role.${role}`),
              }))}
            />
          </div>
        </div>
        <div>
          <Space>
            <Switch
              aria-label={t('notifications:confirmationNotice.accountManager')}
              checked={config.includeAccountManager}
              onChange={(checked) => setConfig({ ...config, includeAccountManager: checked })}
            />
            <Text>{t('notifications:confirmationNotice.accountManager')}</Text>
          </Space>
        </div>
        <div>
          <Text strong>{t('notifications:confirmationNotice.chosenUsers')}</Text>
          <Select
            mode="multiple"
            aria-label={t('notifications:confirmationNotice.chosenUsers')}
            style={{ width: '100%', marginTop: 8 }}
            placeholder={t('notifications:confirmationNotice.chosenUsersPlaceholder')}
            value={config.userIds}
            onChange={(userIds: string[]) => setConfig({ ...config, userIds })}
            optionFilterProp="label"
            options={users.map((user) => ({
              value: user.id,
              label: `${user.name} (${t(`common:userRole.${user.role}`, { defaultValue: user.role })})`,
            }))}
          />
        </div>
        {nobodySelected && (
          <Alert type="warning" showIcon message={t('notifications:confirmationNotice.nobody')} />
        )}
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          {t('notifications:confirmationNotice.save')}
        </Button>
      </Space>
    </Card>
  );
};

export default ConfirmationNoticeRecipientsCard;
